import { z } from 'zod';

export const TEST_DATA_TYPES = ['inline', 'dataset', 'json', 'csv', 'excel', 'secret'] as const;
export type TestDataType = (typeof TEST_DATA_TYPES)[number];

export const TEST_DATA_STATUS = ['active', 'archived'] as const;
export type TestDataStatus = (typeof TEST_DATA_STATUS)[number];

export const TEST_DATA_EXECUTION_MODES = ['single', 'all', 'selected'] as const;
export type TestDataExecutionMode = (typeof TEST_DATA_EXECUTION_MODES)[number];

export const TEST_DATA_SENSITIVITY_LEVELS = ['public', 'internal', 'sensitive', 'secret'] as const;
export type TestDataSensitivity = (typeof TEST_DATA_SENSITIVITY_LEVELS)[number];

export const TEST_DATA_SCOPE_LEVELS = ['project', 'org', 'global'] as const;
export type TestDataScopeLevel = (typeof TEST_DATA_SCOPE_LEVELS)[number];

export const TEST_DATA_SCOPE_ORIGINS = ['project', 'org', 'global'] as const;
export type TestDataScopeOrigin = (typeof TEST_DATA_SCOPE_ORIGINS)[number];

export const TEST_DATA_MAX_ROWS = 5_000;
export const TEST_DATA_MAX_COLUMNS = 50;
export const TEST_DATA_MAX_INLINE_ENTRIES = 100;
export const TEST_DATA_IMPORT_MAX_BYTES = 10 * 1024 * 1024;

export const TestDataColumnSchema = z.object({
  name: z.string().trim().min(1).max(120),
  type: z.enum(['string', 'number', 'boolean', 'date']).default('string'),
});
export type TestDataColumn = z.infer<typeof TestDataColumnSchema>;

export const TestDataInlineEntrySchema = z.object({
  key: z.string().trim().min(1).max(80),
  value: z.string().max(2_000).default(''),
  isSecret: z.boolean().default(false),
});
export type TestDataInlineEntry = z.infer<typeof TestDataInlineEntrySchema>;

export const QualityTestDataSchema = z.object({
  id: z.string().uuid(),
  key: z.string().min(1).max(80),
  name: z.string().min(1).max(200),
  description: z.string().max(2_000).default(''),
  type: z.enum(TEST_DATA_TYPES),
  status: z.enum(TEST_DATA_STATUS),
  columns: z.array(TestDataColumnSchema).default([]),
  rows: z.array(z.record(z.string(), z.string())).default([]),
  inlineEntries: z.array(TestDataInlineEntrySchema).default([]),
  executionMode: z.enum(TEST_DATA_EXECUTION_MODES).default('single'),
  selectedRowIndexes: z.array(z.number().int().nonnegative()).default([]),
  /** For `secret` type — references an existing project variable name. */
  secretVariableName: z.string().max(64).nullable().optional(),
  /** Catalogue-level classification — secret values are never returned via API. */
  sensitivity: z.enum(TEST_DATA_SENSITIVITY_LEVELS).default('public'),
  category: z.string().max(120).default(''),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  version: z.number().int().positive().default(1),
  rowCount: z.number().int().nonnegative().optional(),
  usageCount: z.number().int().nonnegative().optional(),
  scenarioUsageCount: z.number().int().nonnegative().optional(),
  componentUsageCount: z.number().int().nonnegative().optional(),
  automationUsageCount: z.number().int().nonnegative().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdByName: z.string().nullable().optional(),
  updatedByName: z.string().nullable().optional(),
  /** Catalogue scope — project-owned by default; org/global for templates (Feature 4E). */
  scopeLevel: z.enum(TEST_DATA_SCOPE_LEVELS).default('project').optional(),
  scopeOrigin: z.enum(TEST_DATA_SCOPE_ORIGINS).optional(),
  templateId: z.string().uuid().nullable().optional(),
  adoptionId: z.string().uuid().nullable().optional(),
});
export type QualityTestData = z.infer<typeof QualityTestDataSchema>;

export const TestDataListResponseSchema = z.object({
  projectId: z.string().uuid(),
  items: z.array(QualityTestDataSchema),
});
export type TestDataListResponse = z.infer<typeof TestDataListResponseSchema>;

export const TestDataResponseSchema = z.object({
  item: QualityTestDataSchema,
});
export type TestDataResponse = z.infer<typeof TestDataResponseSchema>;

export const CreateTestDataRequestSchema = z.object({
  key: z.string().trim().min(1).max(80),
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2_000).optional(),
  type: z.enum(TEST_DATA_TYPES),
  columns: z.array(TestDataColumnSchema).max(TEST_DATA_MAX_COLUMNS).optional(),
  rows: z.array(z.record(z.string(), z.string())).max(TEST_DATA_MAX_ROWS).optional(),
  inlineEntries: z.array(TestDataInlineEntrySchema).max(TEST_DATA_MAX_INLINE_ENTRIES).optional(),
  executionMode: z.enum(TEST_DATA_EXECUTION_MODES).optional(),
  selectedRowIndexes: z.array(z.number().int().nonnegative()).optional(),
  secretVariableName: z.string().trim().min(1).max(64).nullable().optional(),
  sensitivity: z.enum(TEST_DATA_SENSITIVITY_LEVELS).optional(),
  category: z.string().max(120).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type CreateTestDataRequest = z.infer<typeof CreateTestDataRequestSchema>;

export const PatchTestDataRequestSchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(2_000).optional(),
  status: z.enum(TEST_DATA_STATUS).optional(),
  columns: z.array(TestDataColumnSchema).max(TEST_DATA_MAX_COLUMNS).optional(),
  rows: z.array(z.record(z.string(), z.string())).max(TEST_DATA_MAX_ROWS).optional(),
  inlineEntries: z.array(TestDataInlineEntrySchema).max(TEST_DATA_MAX_INLINE_ENTRIES).optional(),
  executionMode: z.enum(TEST_DATA_EXECUTION_MODES).optional(),
  selectedRowIndexes: z.array(z.number().int().nonnegative()).optional(),
  secretVariableName: z.string().trim().min(1).max(64).nullable().optional(),
  sensitivity: z.enum(TEST_DATA_SENSITIVITY_LEVELS).optional(),
  category: z.string().max(120).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
});
export type PatchTestDataRequest = z.infer<typeof PatchTestDataRequestSchema>;

export const TestDataImportPreviewRequestSchema = z.object({
  format: z.enum(['csv', 'json', 'excel']),
  content: z.string().max(TEST_DATA_IMPORT_MAX_BYTES).optional(),
  sheetName: z.string().max(120).optional(),
});
export type TestDataImportPreviewRequest = z.infer<typeof TestDataImportPreviewRequestSchema>;

export const TestDataImportPreviewResponseSchema = z.object({
  columns: z.array(TestDataColumnSchema),
  rows: z.array(z.record(z.string(), z.string())),
  sheetNames: z.array(z.string()).optional(),
  selectedSheet: z.string().optional(),
  warnings: z.array(z.string()).default([]),
});
export type TestDataImportPreviewResponse = z.infer<typeof TestDataImportPreviewResponseSchema>;

export const TestDataUsageResponseSchema = z.object({
  itemId: z.string().uuid(),
  scenarioCount: z.number().int().nonnegative(),
  componentCount: z.number().int().nonnegative(),
  automationCount: z.number().int().nonnegative(),
  usageCount: z.number().int().nonnegative(),
  scenarios: z.array(z.object({ id: z.string().uuid(), key: z.string(), title: z.string() })).default([]),
  components: z.array(z.object({ id: z.string().uuid(), key: z.string(), name: z.string() })).default([]),
  automationFlows: z.array(z.object({ id: z.string().uuid(), name: z.string() })).default([]),
});
export type TestDataUsageResponse = z.infer<typeof TestDataUsageResponseSchema>;

export const TestDataRunContextResponseSchema = z.object({
  projectId: z.string().uuid(),
  flowId: z.string().uuid().nullable().optional(),
  scenarioId: z.string().uuid().nullable().optional(),
  /** Nested map keyed by test-data key — values are row objects or inline maps. Secrets are never included. */
  data: z.record(z.string(), z.unknown()),
  environment: z.record(z.string(), z.string()).default({}),
  /** Row sets for data-loop iteration when executionMode is all or selected. */
  datasets: z.record(z.string(), z.array(z.record(z.string(), z.string()))).default({}),
  /** Run-scoped resolver bundle for per-step resolution when step overrides exist. Secrets never included. */
  resolverBundle: z
    .object({
      catalogue: z.array(
        z.object({
          id: z.string().uuid(),
          key: z.string(),
          type: z.enum(TEST_DATA_TYPES),
          status: z.enum(TEST_DATA_STATUS),
          rows: z.array(z.record(z.string(), z.string())),
          inlineEntries: z.array(TestDataInlineEntrySchema),
          executionMode: z.enum(TEST_DATA_EXECUTION_MODES),
          selectedRowIndexes: z.array(z.number().int().nonnegative()),
          sensitivity: z.enum(TEST_DATA_SENSITIVITY_LEVELS),
        }),
      ),
      overrides: z.array(
        z.object({
          id: z.string().uuid(),
          projectId: z.string().uuid(),
          catalogueItemId: z.string().uuid(),
          catalogueKey: z.string(),
          scopeKind: z.enum(['flow', 'scenario', 'step', 'global']),
          scopeId: z.string().uuid(),
          enabled: z.boolean(),
          rowIndex: z.number().int().nonnegative().nullable(),
          selectedRowIndexes: z.array(z.number().int().nonnegative()),
        }),
      ),
      context: z.object({
        projectId: z.string().uuid(),
        flowId: z.string().uuid().nullable().optional(),
        scenarioId: z.string().uuid().nullable().optional(),
      }),
      hasStepOverrides: z.boolean(),
      inheritedTemplates: z
        .array(
          z.object({
            item: z.object({
              id: z.string().uuid(),
              key: z.string(),
              type: z.enum(TEST_DATA_TYPES),
              status: z.enum(TEST_DATA_STATUS),
              rows: z.array(z.record(z.string(), z.string())),
              inlineEntries: z.array(TestDataInlineEntrySchema),
              executionMode: z.enum(TEST_DATA_EXECUTION_MODES),
              selectedRowIndexes: z.array(z.number().int().nonnegative()),
              sensitivity: z.enum(TEST_DATA_SENSITIVITY_LEVELS),
            }),
            scopeLevel: z.enum(['org', 'global']),
          }),
        )
        .optional(),
      templateAdoptions: z
        .array(
          z.object({
            templateId: z.string().uuid(),
            templateKey: z.string(),
            enabled: z.boolean(),
            rowIndex: z.number().int().nonnegative().nullable(),
            selectedRowIndexes: z.array(z.number().int().nonnegative()),
          }),
        )
        .optional(),
    })
    .optional(),
});
export type TestDataRunContextResponse = z.infer<typeof TestDataRunContextResponseSchema>;

export const TestDataExportEntrySchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  type: z.enum(TEST_DATA_TYPES),
  status: z.enum(TEST_DATA_STATUS),
  sensitivity: z.enum(TEST_DATA_SENSITIVITY_LEVELS),
  category: z.string(),
  tags: z.array(z.string()),
  columns: z.array(TestDataColumnSchema),
  rows: z.array(z.record(z.string(), z.string())),
  inlineEntries: z.array(TestDataInlineEntrySchema),
  executionMode: z.enum(TEST_DATA_EXECUTION_MODES),
  selectedRowIndexes: z.array(z.number().int().nonnegative()),
  secretVariableName: z.string().nullable().optional(),
  version: z.number().int().positive(),
});
export type TestDataExportEntry = z.infer<typeof TestDataExportEntrySchema>;

export const TestDataExportResponseSchema = z.object({
  projectId: z.string().uuid(),
  exportedAt: z.string(),
  format: z.enum(['json', 'csv']),
  items: z.array(TestDataExportEntrySchema),
});
export type TestDataExportResponse = z.infer<typeof TestDataExportResponseSchema>;
